import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { serviceLabel } from "@/lib/api";
import { Users, DollarSign, Inbox, Clock, BadgeCheck, Star, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [leads, setLeads] = useState([]);
  const [tab, setTab] = useState("members");
  const [categories, setCategories] = useState([]);
  const [newTrade, setNewTrade] = useState("");

  useEffect(() => { if (!loading && (!user || user.role !== "admin")) nav("/"); }, [loading, user, nav]);
  const load = () => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/businesses").then(({ data }) => setBusinesses(data)).catch(() => {});
    api.get("/admin/leads").then(({ data }) => setLeads(data)).catch(() => {});
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
  };
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  if (loading || !stats) return <div className="min-h-[60vh] grid place-items-center text-[color:var(--ink-3)]">Loading admin...</div>;

  const approve = async (id) => { await api.post(`/admin/businesses/${id}/approve`); toast.success("Business approved"); load(); };
  const feature = async (id) => { await api.post(`/admin/businesses/${id}/toggle-feature`); load(); };
  const addCredits = async (id) => { await api.post(`/admin/businesses/${id}/credits?amount=5`); toast.success("Added 5 credits"); load(); };
  const addTrade = async () => {
    if (!newTrade.trim()) return;
    try {
      const { data } = await api.post("/admin/categories", { label: newTrade.trim() });
      setCategories(data); setNewTrade(""); toast.success("Trade added");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const cards = [
    { label: "Revenue", value: `$${stats.revenue.toLocaleString()}`, icon: DollarSign, color: "#047857" },
    { label: "Members", value: stats.total_businesses, icon: Users, color: "#0F172A" },
    { label: "Active Subs", value: stats.active_subscriptions, icon: TrendingUp, color: "#D97706" },
    { label: "Total Leads", value: stats.total_leads, icon: Inbox, color: "#047857" },
    { label: "Pending Approvals", value: stats.pending_approvals, icon: Clock, color: "#D97706" },
    { label: "Claimed Leads", value: stats.claimed_leads, icon: BadgeCheck, color: "#0F172A" },
  ];

  const chartData = [
    { name: "Members", value: stats.total_businesses },
    { name: "Active Subs", value: stats.active_subscriptions },
    { name: "Leads", value: stats.total_leads },
    { name: "Claimed", value: stats.claimed_leads },
    { name: "Pending", value: stats.pending_approvals },
  ];
  const colors = ["#047857", "#D97706", "#0F172A", "#10B981", "#F59E0B"];

  return (
    <div className="container-x py-12" data-testid="admin-dashboard">
      <div className="mb-8">
        <span className="text-xs font-mono uppercase tracking-wider text-[color:var(--emerald)]">Admin Console</span>
        <h1 className="font-display text-3xl font-extrabold text-[color:var(--navy)] mt-1">Platform Overview</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
        {cards.map((c) => (
          <div key={c.label} className="card-soft p-5" data-testid={`admin-stat-${c.label.toLowerCase().replace(/ /g, "-")}`}>
            <c.icon className="w-5 h-5 mb-2" style={{ color: c.color }} />
            <div className="font-display text-2xl font-extrabold text-[color:var(--navy)]">{c.value}</div>
            <div className="text-xs text-[color:var(--ink-3)]">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="card-soft p-6 mb-8">
        <h3 className="font-display font-bold text-lg text-[color:var(--navy)] mb-4">Platform metrics</h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData}>
            <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip cursor={{ fill: "#F1F5F9" }} />
            <Bar dataKey="value" radius={[8, 8, 0, 0]}>
              {chartData.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="card-soft p-6 mb-8" data-testid="admin-trades-card">
        <h3 className="font-display font-bold text-lg text-[color:var(--navy)] mb-1">Service Trades</h3>
        <p className="text-sm text-[color:var(--ink-3)] mb-4">Add new service categories that pros can list under and homeowners can request quotes for.</p>
        <div className="flex flex-wrap gap-2 mb-4">
          {categories.map((c) => (
            <span key={c.key} className={`text-xs font-semibold px-3 py-1.5 rounded-full ${c.custom ? "badge-verified" : "bg-[color:var(--bg-3)] text-[color:var(--ink-2)]"}`} data-testid={`admin-trade-${c.key}`}>{c.label}{c.custom ? " ★" : ""}</span>
          ))}
        </div>
        <div className="flex gap-2 max-w-md">
          <Input value={newTrade} onChange={(e) => setNewTrade(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTrade()} placeholder="e.g. Solar, Plumbing, Electrical" data-testid="admin-new-trade-input" />
          <Button onClick={addTrade} className="btn-primary rounded-full px-6" data-testid="admin-add-trade-button">Add</Button>
        </div>
      </div>

      <div className="flex gap-2 mb-6">
        <button onClick={() => setTab("members")} data-testid="admin-tab-members" className={`px-5 py-2 rounded-full text-sm font-semibold ${tab === "members" ? "btn-primary" : "bg-[color:var(--bg-3)] text-[color:var(--ink-2)]"}`}>Members ({businesses.length})</button>
        <button onClick={() => setTab("leads")} data-testid="admin-tab-leads" className={`px-5 py-2 rounded-full text-sm font-semibold ${tab === "leads" ? "btn-primary" : "bg-[color:var(--bg-3)] text-[color:var(--ink-2)]"}`}>Leads ({leads.length})</button>
      </div>

      {tab === "members" ? (
        <div className="card-soft overflow-hidden">
          <table className="w-full text-sm" data-testid="admin-members-table">
            <thead className="bg-[color:var(--bg-3)] text-left text-[color:var(--ink-3)]">
              <tr><th className="p-4">Business</th><th className="p-4">City</th><th className="p-4">Plan</th><th className="p-4">Credits</th><th className="p-4">Status</th><th className="p-4 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {businesses.map((b) => (
                <tr key={b.id} className="border-t border-slate-100" data-testid={`admin-member-row-${b.id}`}>
                  <td className="p-4 font-medium text-[color:var(--navy)]">{b.name} {b.featured && <Star className="w-3.5 h-3.5 inline fill-amber-400 text-amber-400" />}</td>
                  <td className="p-4 text-[color:var(--ink-2)]">{b.city}</td>
                  <td className="p-4 capitalize text-[color:var(--ink-2)]">{b.subscription_tier || "—"}</td>
                  <td className="p-4 text-[color:var(--ink-2)]">{b.credits}</td>
                  <td className="p-4">{b.approved ? <span className="badge-verified px-2 py-0.5 rounded-full text-xs">Approved</span> : <span className="badge-pest px-2 py-0.5 rounded-full text-xs">Pending</span>}</td>
                  <td className="p-4">
                    <div className="flex gap-2 justify-end">
                      {!b.approved && <Button onClick={() => approve(b.id)} data-testid={`admin-approve-member-button-${b.id}`} className="btn-primary rounded-full h-8 px-3 text-xs">Approve</Button>}
                      <Button onClick={() => feature(b.id)} variant="outline" className="rounded-full h-8 px-3 text-xs" data-testid={`admin-feature-button-${b.id}`}>{b.featured ? "Unfeature" : "Feature"}</Button>
                      <Button onClick={() => addCredits(b.id)} variant="outline" className="rounded-full h-8 px-3 text-xs" data-testid={`admin-credits-button-${b.id}`}>+5 credits</Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card-soft overflow-hidden">
          <table className="w-full text-sm" data-testid="admin-leads-table">
            <thead className="bg-[color:var(--bg-3)] text-left text-[color:var(--ink-3)]">
              <tr><th className="p-4">Customer</th><th className="p-4">Service</th><th className="p-4">City</th><th className="p-4">Urgency</th><th className="p-4">Matched</th><th className="p-4">Claimed by</th><th className="p-4">Status</th></tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-t border-slate-100" data-testid={`admin-lead-row-${l.id}`}>
                  <td className="p-4 font-medium text-[color:var(--navy)]">{l.name}</td>
                  <td className="p-4 text-[color:var(--ink-2)]">{serviceLabel(l.service_type)}</td>
                  <td className="p-4 text-[color:var(--ink-2)]">{l.city}</td>
                  <td className="p-4 uppercase text-xs text-[color:var(--ink-2)]">{l.urgency}</td>
                  <td className="p-4 text-[color:var(--ink-2)]">{(l.matched_businesses || []).length}</td>
                  <td className="p-4 text-[color:var(--ink-2)]">{(l.claimed_by || []).length}</td>
                  <td className="p-4">{l.status === "claimed" ? <span className="badge-verified px-2 py-0.5 rounded-full text-xs">Claimed</span> : <span className="badge-ins px-2 py-0.5 rounded-full text-xs">New</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
